import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { useScheduleVisit } from '@/hooks/useVisits';
import { useTransitionIssue } from '@/hooks/useServiceIssues';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedPropertyId?: string;
  /** When scheduling from an issue, link the visit to this issue */
  issueId?: string;
  /** Display context in dialog header */
  issueTitle?: string;
  /** Called after both visit creation and issue transition succeed */
  onIssueScheduled?: () => void;
  /** Pre-fill visit type (e.g. from recommendation conversion) */
  prefilledVisitType?: string;
  /** Pre-fill internal notes (e.g. recommendation context for technician) */
  prefilledInternalNotes?: string;
}

export function ScheduleVisitDialog({ open, onOpenChange, preselectedPropertyId, issueId, issueTitle, onIssueScheduled, prefilledVisitType, prefilledInternalNotes }: Props) {
  const { properties } = useApp();
  const schedule = useScheduleVisit();
  const transitionIssue = useTransitionIssue();

  const [propertyId, setPropertyId] = useState(preselectedPropertyId ?? '');
  const [visitType, setVisitType] = useState(prefilledVisitType ?? (issueId ? 'repair' : 'preventive'));
  const [scheduledAt, setScheduledAt] = useState('');
  const [technicianName, setTechnicianName] = useState('');
  const [internalNotes, setInternalNotes] = useState(prefilledInternalNotes ?? '');
  const [customerNotes, setCustomerNotes] = useState('');

  const reset = () => {
    setPropertyId(preselectedPropertyId ?? '');
    setVisitType(prefilledVisitType ?? (issueId ? 'repair' : 'preventive'));
    setScheduledAt('');
    setTechnicianName('');
    setInternalNotes(prefilledInternalNotes ?? '');
    setCustomerNotes('');
  };

  const handleSchedule = () => {
    if (!propertyId || !scheduledAt) {
      toast.error('Property and scheduled date are required');
      return;
    }

    const scheduledIso = new Date(scheduledAt).toISOString();
    const scheduledDate = scheduledIso.split('T')[0]; // YYYY-MM-DD for issue scheduled_for

    schedule.mutate(
      {
        propertyId,
        visitType,
        scheduledAt: scheduledIso,
        technicianName: technicianName.trim() || undefined,
        resolvedIssueIds: issueId ? [issueId] : undefined,
        internalNotes: internalNotes.trim() || undefined,
      },
      {
        onSuccess: () => {
          if (issueId) {
            // Step 2: transition issue to scheduled (fires homeowner notification via useTransitionIssue)
            transitionIssue.mutate(
              {
                issueId,
                newStatus: 'scheduled',
                scheduledFor: scheduledDate,
                customerNotes: customerNotes.trim() || undefined,
              },
              {
                onSuccess: () => {
                  toast.success('Visit scheduled and issue updated');
                  reset();
                  onOpenChange(false);
                  onIssueScheduled?.();
                },
                onError: (err) => {
                  toast.warning(
                    'Visit was created, but the issue status could not be updated. Please refresh and update the issue manually.',
                    { description: err instanceof Error ? err.message : undefined },
                  );
                  reset();
                  onOpenChange(false);
                  onIssueScheduled?.();
                },
              }
            );
          } else {
            toast.success('Visit scheduled');
            reset();
            onOpenChange(false);
          }
        },
        onError: (err) => {
          toast.error(`Failed to schedule: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      }
    );
  };

  const isPending = schedule.isPending || transitionIssue.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule Visit</DialogTitle>
          {issueTitle && (
            <DialogDescription>
              Scheduling visit for: {issueTitle}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger>
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Visit Type</Label>
            <Select value={visitType} onValueChange={setVisitType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="preventive">Preventive</SelectItem>
                <SelectItem value="repair">Repair</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
                <SelectItem value="commissioning">Commissioning</SelectItem>
                <SelectItem value="warranty_inspection">Warranty Inspection</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Scheduled Date & Time</Label>
            <Input
              type="datetime-local"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Technician Name</Label>
            <Input
              placeholder="Optional — name of assigned tech"
              value={technicianName}
              onChange={e => setTechnicianName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Internal Notes</Label>
            <Textarea
              placeholder="Optional — notes for the tech"
              value={internalNotes}
              onChange={e => setInternalNotes(e.target.value)}
              rows={3}
            />
          </div>

          {issueId && (
            <div className="space-y-2">
              <Label>Customer-Visible Note</Label>
              <Textarea
                placeholder="Optional — included in homeowner notification"
                value={customerNotes}
                onChange={e => setCustomerNotes(e.target.value)}
                rows={2}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSchedule} disabled={isPending}>
            {isPending ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
